import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function GardenKeeper() {
    const [key, setKey] = useState(sessionStorage.getItem("keeper_key") || "");
    const [authed, setAuthed] = useState(false);
    const [letters, setLetters] = useState([]);
    const [gateError, setGateError] = useState("");

    const load = useCallback(async (k) => {
        try {
            const { data } = await api.get(`/letters/all?key=${encodeURIComponent(k)}`);
            setLetters(data.letters);
            sessionStorage.setItem("keeper_key", k);
            setAuthed(true);
            setGateError("");
            return true;
        } catch (e) {
            setGateError("That key does not open the gate.");
            return false;
        }
    }, []);

    useEffect(() => {
        if (key) load(key);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const approve = async (letterId) => {
        try {
            await api.post(`/letters/${letterId}/approve?key=${encodeURIComponent(key)}`);
            toast.success("The letter now rests in the garden.");
            load(key);
        } catch (e) {
            toast.error("Could not approve. Try again.");
        }
    };

    const remove = async (letterId) => {
        try {
            await api.delete(`/letters/${letterId}?key=${encodeURIComponent(key)}`);
            toast.success("The letter has been returned to the wind.");
            load(key);
        } catch (e) {
            toast.error("Could not remove. Try again.");
        }
    };

    if (!authed) {
        return (
            <div className="flex min-h-screen items-center justify-center px-6" data-testid="keeper-gate">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="w-full max-w-md text-center"
                >
                    <p className="font-cinzel text-xs tracking-[0.4em] text-[#b89860] uppercase">The Garden Keeper</p>
                    <h1 className="font-cinzel mt-5 text-3xl text-[#f3e5ab]">Only the Author May Enter</h1>
                    <p className="font-cormorant mt-4 text-lg italic text-[#a3b899]">
                        Speak the key, and tend the letters left at the wall.
                    </p>
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            load(key);
                        }}
                        className="mt-10 space-y-4"
                    >
                        <input
                            type="password"
                            value={key}
                            onChange={(e) => setKey(e.target.value)}
                            placeholder="Keeper's key"
                            data-testid="keeper-key-input"
                            className="input-eden font-cormorant w-full rounded-sm px-5 py-4 text-lg"
                        />
                        <button
                            type="submit"
                            data-testid="keeper-enter-button"
                            className="btn-eden w-full rounded-sm bg-gradient-to-br from-[rgba(160,120,60,0.9)] to-[rgba(120,85,40,0.9)] px-8 py-4 text-xs text-[#f5e6c8]"
                        >
                            Enter the Garden
                        </button>
                    </form>
                    {gateError && (
                        <p className="font-cormorant mt-4 text-base italic text-[#c84b31]" data-testid="keeper-gate-error">
                            {gateError}
                        </p>
                    )}
                    <Link to="/" className="font-cinzel mt-10 inline-block text-[0.65rem] tracking-[0.3em] text-[#6a5a40] uppercase hover:text-[#d4af37]" data-testid="keeper-home-link">
                        Return to the Garden
                    </Link>
                </motion.div>
            </div>
        );
    }

    const pending = letters.filter((l) => !l.approved);
    const approved = letters.filter((l) => l.approved);

    const LetterRow = ({ letter, isPending }) => (
        <div className="letter-note rounded-sm p-5" data-testid={`keeper-letter-${letter.letter_id}`}>
            <p className="font-cormorant text-lg italic leading-relaxed">&ldquo;{letter.message}&rdquo;</p>
            <p className="font-cinzel mt-3 text-[0.6rem] tracking-[0.3em] text-[#8a6f3c] uppercase">— {letter.name}</p>
            <div className="mt-4 flex gap-3">
                {isPending && (
                    <button
                        onClick={() => approve(letter.letter_id)}
                        data-testid={`keeper-approve-${letter.letter_id}`}
                        className="font-cinzel rounded-sm border border-[#4a7a3c] bg-[#2f5d3a] px-4 py-2 text-[0.6rem] tracking-[0.25em] text-[#e8f5d8] uppercase transition-colors duration-300 hover:bg-[#3f7048]"
                    >
                        Let It Rest
                    </button>
                )}
                <button
                    onClick={() => remove(letter.letter_id)}
                    data-testid={`keeper-remove-${letter.letter_id}`}
                    className="font-cinzel rounded-sm border border-[#8a4a3c] bg-transparent px-4 py-2 text-[0.6rem] tracking-[0.25em] text-[#a05040] uppercase transition-colors duration-300 hover:bg-[#4a2420]"
                >
                    Return to the Wind
                </button>
            </div>
        </div>
    );

    return (
        <div className="mx-auto min-h-screen max-w-4xl px-6 py-20" data-testid="keeper-page">
            <p className="font-cinzel text-center text-xs tracking-[0.4em] text-[#b89860] uppercase">The Garden Keeper</p>
            <h1 className="font-cinzel mt-5 text-center text-3xl text-[#f3e5ab] sm:text-4xl">Tend the Letters</h1>

            <section className="mt-14">
                <h2 className="font-cinzel text-lg tracking-[0.2em] text-[#d4af37]" data-testid="keeper-pending-heading">
                    Awaiting Your Reading ({pending.length})
                </h2>
                <div className="mt-6 space-y-5">
                    {pending.length === 0 && (
                        <p className="font-cormorant text-lg italic text-[#8a7a60]">No letters are waiting. The garden is at peace.</p>
                    )}
                    {pending.map((l) => (
                        <LetterRow key={l.letter_id} letter={l} isPending />
                    ))}
                </div>
            </section>

            <section className="mt-16">
                <h2 className="font-cinzel text-lg tracking-[0.2em] text-[#a3b899]" data-testid="keeper-approved-heading">
                    Resting in the Garden ({approved.length})
                </h2>
                <div className="mt-6 space-y-5 opacity-90">
                    {approved.map((l) => (
                        <LetterRow key={l.letter_id} letter={l} isPending={false} />
                    ))}
                </div>
            </section>

            <div className="mt-16 text-center">
                <Link to="/" className="font-cinzel text-[0.65rem] tracking-[0.3em] text-[#6a5a40] uppercase hover:text-[#d4af37]" data-testid="keeper-home-link-2">
                    Return to the Garden
                </Link>
            </div>
        </div>
    );
}
